import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, merge } from 'rxjs';
import { takeUntil, first } from 'rxjs/operators';
import { find } from 'lodash';

import { AssististService } from '../assistist.service';
import { SessionService } from 'src/app/session.service';
import { NotificationService } from 'src/app/notifications/notification.service';
import { getAssististDocumentLabels } from '../document-labels';

interface CategoryTagData {
  document_category_id: number;
  document_category_name: string;
  distinct_tags: string[];
}

@Component({
  selector: 'assistist-documents-preview',
  templateUrl: './documents-preview.component.html',
  styleUrls: ['./documents-preview.component.scss']
})
export class DocumentsPreviewComponent implements OnInit, OnDestroy {
  categoriesData: CategoryTagData[] = [];
  loading = false;
  featureType: string = '';
  private destroy$ = new Subject<void>();
  private currentIntegration: any;
  private integrationID!: number;

  // Getter methods for display text
  get itemsText(): string {
    return getAssististDocumentLabels(this.featureType).plural;
  }

  get previewTitle(): string {
    return `${this.itemsText} Categories & Tags Preview`;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private svc: AssististService,
    private session: SessionService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    // Subscribe to route parameter and data changes
    merge(
      this.activatedRoute.params,
      this.activatedRoute.data
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateFromRouteParams();
      this.loadCategoriesData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateFromRouteParams(): void {
    // Get the feature type from the route
    const featureType = this.activatedRoute.snapshot.params['featureType'];
    const providerType = featureType === 'tripist'
      ? 'tripist_assistant'
      : featureType === 'aura'
        ? 'aura_assistant'
        : 'explorist_assistant';

    this.currentIntegration = find(
      this.activatedRoute.snapshot.data.integrations,
      (i: any) => i.provider.type === providerType
    );
    this.integrationID = this.currentIntegration.id;
    this.featureType = featureType;
  }

  private get currentAccountID(): number {
    return this.session.currentAccountValue!.accountID;
  }

  private loadCategoriesData(): void {
    this.loading = true;
    
    this.svc.getCategoriesTags(this.currentAccountID, this.integrationID)
      .pipe(
        first(),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: any) => {
          this.categoriesData = response.body || response;
          this.loading = false;
        },
        error: (error) => {
          this.notification.openErrorSnackBar('Error loading categories and tags data');
          this.loading = false;
        }
      });
  }
} 